import React, { useEffect, useRef, forwardRef } from "react";
import { Label } from "../Label";
import { InputProps } from "./Input.types";
import { Error } from "./Error";
import { InputComponent } from "./InputComponent";
import { Root } from "./Root";

/**
 * Complete Input component with label, validation, and error handling
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      error,
      label,
      required,
      id,
      disabled,
      isValid = true,
      width,
      autofocus = false,
      analyticId,
      ...props
    },
    forwardedRef
  ) => {
    const internalRef = useRef<HTMLInputElement>(null);

    // Use forwarded ref if provided, otherwise use internal ref
    const ref = forwardedRef || internalRef;

    useEffect(() => {
      if (!autofocus) return;

      const inputElement = typeof ref === "function" ? null : ref?.current;
      if (inputElement?.focus) {
        inputElement.focus();
      }
    }, [ref, autofocus]);

    return (
      <Root className={className}>
        {label && (
          <Label
            required={required}
            htmlFor={id}
            disabled={disabled}
            isValid={isValid}
          >
            {label}
          </Label>
        )}
        <InputComponent
          ref={ref}
          id={id}
          data-pendo-identifier={analyticId}
          required={required}
          disabled={disabled}
          isValid={isValid}
          width={width}
          {...props}
        />
        {error && <Error>{error}</Error>}
      </Root>
    );
  }
);

Input.displayName = "Input";
